import { Fragment, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { CmspCaseDetails } from './contract';
import { format, useStrings } from './strings';
import './caseDetails.css';

export function readCaseDetails(value: unknown): CmspCaseDetails | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  if (!('caseNumber' in value) || typeof value.caseNumber !== 'string' || !value.caseNumber.trim()
    || !('title' in value) || typeof value.title !== 'string' || !value.title.trim()
    || !('summary' in value) || typeof value.summary !== 'string' || !value.summary.trim()) return undefined;
  return { caseNumber: value.caseNumber, title: value.title, summary: value.summary };
}

/** Only structured case confirmations become interactive; ordinary numbers remain prose. */
export function CaseDetailsMessage({ text, caseDetails, renderText }: {
  text: string;
  caseDetails?: CmspCaseDetails | null;
  renderText: (text: string) => ReactNode;
}) {
  const labels = useStrings().caseDetails;
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const [expandedCase, setExpandedCase] = useState<string | null>(null);
  const details = readCaseDetails(caseDetails);
  const paragraphs = text.split(/(\n\s*\n)/);
  const confirmationIndex = details ? paragraphs.findIndex(part => part.includes(details.caseNumber)) : -1;
  if (!details || confirmationIndex < 0) return <>{renderText(text)}</>;

  const expanded = expandedCase === details.caseNumber;
  const confirmation = paragraphs[confirmationIndex];
  const numberIndex = confirmation.indexOf(details.caseNumber);
  const close = () => {
    setExpandedCase(null);
    button.current?.focus();
  };
  return (
    <div className="cmsp-case-message" onKeyDown={event => {
      if (event.key === 'Escape' && expanded) {
        event.preventDefault();
        event.stopPropagation();
        close();
      }
    }}>
      {paragraphs.map((part, index) => index === confirmationIndex ? (
        <Fragment key={index}>
          <div className="cmsp-case-confirmation">
            {confirmation.slice(0, numberIndex)}
            <button ref={button} type="button" className="cmsp-case-number"
              aria-expanded={expanded} aria-controls={id}
              aria-label={format(labels.toggle, { caseNumber: details.caseNumber })}
              onClick={() => setExpandedCase(expanded ? null : details.caseNumber)}>
              {details.caseNumber}
            </button>
            {confirmation.slice(numberIndex + details.caseNumber.length)}
          </div>
          <section id={id} className="cmsp-case-card" hidden={!expanded}
            aria-label={format(labels.heading, { caseNumber: details.caseNumber })}>
            <div className="cmsp-case-card-header">
              <strong>{format(labels.heading, { caseNumber: details.caseNumber })}</strong>
              <button type="button" className="cmsp-case-close" onClick={close}>{labels.close}</button>
            </div>
            <dl>
              <dt>{labels.title}</dt>
              <dd>{details.title}</dd>
              <dt>{labels.summary}</dt>
              <dd>{details.summary}</dd>
            </dl>
          </section>
        </Fragment>
      ) : <Fragment key={index}>{renderText(part)}</Fragment>)}
    </div>
  );
}
